import { median } from '../core/math';
import { Ring } from '../core/ring';
import type { PoseFrame } from '../pose/types';
import { DEFENSE_TUNING } from './defense';
import { PUNCH_TUNING } from './punch';
import { TUNABLES } from './tuning';

/**
 * MOVEMENT PROFILER — fit the thresholds to the person, not the person to the
 * thresholds.
 *
 * The competitive read that produced this: the shipped camera-fitness products
 * (Kemtai, Nex) ask the user to configure nothing. Requiring someone to open a
 * diagnostics screen and drag sliders before their punches register is not a
 * power-user feature, it is a defect with a UI in front of it.
 *
 * We have an advantage those products do not: everything is measured in
 * calibrated units, so we can profile ONE person in fifteen seconds and set
 * their gates from their own movement, rather than shipping a population
 * average that fits nobody in particular. A tall slow puncher and a short snappy
 * one produce very different peak extension rates; a single default cannot serve
 * both, and the default is what "it does not read my movements" usually means.
 *
 * Also relevant: FightCamp's wrist IMUs reportedly struggle to read hooks and
 * uppercuts at all. Wrist accelerometers have far better signal-to-noise than a
 * webcam, which says the honest difficulty here is real and not a bug in our
 * maths — so the sensible response is to measure each user rather than to keep
 * hunting for a universal constant that does not exist.
 */

export type MoveKind = 'punch' | 'defence';

export interface Peaks {
  extRate: number;
  gloveSpeed: number;
  travel: number;
  headDepth: number;
  headSpeed: number;
}

export interface Attempt {
  peaks: Peaks;
  startedAt: number;
  closedAt: number;
}

export const zeroPeaks = (): Peaks => ({ extRate: 0, gloveSpeed: 0, travel: 0, headDepth: 0, headSpeed: 0 });

/**
 * Activity level at which an attempt starts being tracked.
 *
 * MUST always be strictly below the gate it shadows. If it ever rose above,
 * an attempt could only open once it was already passing, and the profiler
 * would lose the ability to observe movements that FAILED — which are exactly
 * the ones it needs to see in order to widen a gate.
 */
export function attemptFloor(kind: MoveKind, gate: number): number {
  return kind === 'punch' ? Math.min(0.9, gate * 0.4) : Math.min(0.05, gate * 0.35);
}

/**
 * Watches for discrete movements and records the peak of every gated signal
 * over each one, whether or not the recogniser fired.
 */
export class MovementProfiler {
  readonly attempts: Attempt[] = [];
  private live: Peaks = zeroPeaks();
  private open = false;
  private activeSince = 0;
  private quietSince = 0;
  private extWindow = { left: new Ring<number>(20), right: new Ring<number>(20) };

  constructor(public kind: MoveKind) {}

  reset(): void {
    this.attempts.length = 0;
    this.live = zeroPeaks();
    this.open = false;
    this.quietSince = 0;
    this.extWindow.left.clear();
    this.extWindow.right.clear();
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** Peaks of the movement currently in progress, for live display. */
  get current(): Peaks {
    return this.live;
  }

  /** Feed a frame. Returns a completed attempt on the frame it closes. */
  update(f: PoseFrame): Attempt | null {
    let activity: number;

    if (this.kind === 'punch') {
      this.extWindow.left.push(f.arms.left.extension);
      this.extWindow.right.push(f.arms.right.extension);
      const l = f.arms.left;
      const r = f.arms.right;
      // Whichever arm is doing more. Avoids depending on a stance guess.
      const busy = l.speed >= r.speed ? 'left' : 'right';
      const arm = f.arms[busy];
      const xs = this.extWindow[busy].toArray();
      const swing = xs.length > 2 ? Math.max(...xs) - Math.min(...xs) : 0;
      activity = arm.speed;
      this.live.extRate = Math.max(this.live.extRate, arm.extensionRate);
      this.live.gloveSpeed = Math.max(this.live.gloveSpeed, arm.speed);
      this.live.travel = Math.max(this.live.travel, swing);
    } else {
      const planar = Math.hypot(f.headOffset.x, f.headOffset.y);
      const depth = Math.max(planar, Math.max(0, f.pullDepth));
      const speed = Math.max(Math.hypot(f.headVel.x, f.headVel.y, f.headVel.z), Math.abs(f.pullRate));
      activity = depth;
      this.live.headDepth = Math.max(this.live.headDepth, depth);
      this.live.headSpeed = Math.max(this.live.headSpeed, speed);
    }

    const floor = attemptFloor(
      this.kind,
      this.kind === 'punch' ? PUNCH_TUNING.minOnsetSpeed : DEFENSE_TUNING.enter,
    );

    if (activity > floor) {
      if (!this.open) {
        this.open = true;
        this.activeSince = f.t;
        this.live = zeroPeaks();
      }
      this.quietSince = 0;
      return null;
    }

    if (!this.open) return null;
    if (this.quietSince === 0) this.quietSince = f.t;
    if (f.t - this.quietSince <= 280) return null;

    const lasted = this.quietSince - this.activeSince;
    this.open = false;
    this.quietSince = 0;
    const peaks = { ...this.live };
    this.live = zeroPeaks();
    // Too brief to be a movement — almost certainly a tracking spike.
    if (lasted < 80) return null;

    const a: Attempt = { peaks, startedAt: this.activeSince, closedAt: f.t };
    this.attempts.push(a);
    if (this.attempts.length > 60) this.attempts.shift();
    return a;
  }
}

export interface Fit {
  tunableId: string;
  label: string;
  from: number;
  to: number;
  observed: number;
}

/** Fraction of the observed median each gate is set to. */
const HEADROOM: Record<string, number> = {
  'punch.rateOn': 0.55,
  'punch.minOnsetSpeed': 0.5,
  'punch.minTravel': 0.6,
  'punch.minPeakSpeed': 0.55,
  'defence.enter': 0.6,
  'defence.minSpeed': 0.5,
};

const GATE_SIGNAL: Record<string, keyof Peaks> = {
  'punch.rateOn': 'extRate',
  'punch.minOnsetSpeed': 'gloveSpeed',
  'punch.minTravel': 'travel',
  'punch.minPeakSpeed': 'gloveSpeed',
  'defence.enter': 'headDepth',
  'defence.minSpeed': 'headSpeed',
};

/** Defaults captured at module load, used to bound how far a fit may stray. */
const DEFAULTS: Record<string, number> = Object.fromEntries(TUNABLES.map((t) => [t.id, t.get()]));

export const MIN_ATTEMPTS = 3;

/**
 * Derive thresholds from observed movement.
 *
 * Two deliberate safety properties:
 *
 *  - MEDIAN, not mean or min. One flailing rep must not raise the bar, and one
 *    half-hearted rep must not drop it through the floor.
 *
 *  - ASYMMETRIC CLAMP: may loosen to 0.4x the default, but may only tighten to
 *    1.15x. The two failure directions are not equally bad. Too loose means
 *    occasional false positives, which are annoying but visible and recoverable.
 *    Too tight means your punches do not register at all, which reads as the
 *    game being broken and cannot be fixed by trying harder. On top of that,
 *    warm-up reps are deliberate and large while in-fight punches are smaller
 *    and sloppier, so a symmetric fit would systematically over-tighten. The
 *    lower bound still exists so that a warm-up where someone barely moved
 *    cannot drive the gates to zero and turn every breath into a punch.
 */
export function fitThresholds(kind: MoveKind, attempts: Attempt[]): Fit[] {
  if (attempts.length < MIN_ATTEMPTS) return [];
  const ids = kind === 'punch'
    ? ['punch.rateOn', 'punch.minOnsetSpeed', 'punch.minTravel', 'punch.minPeakSpeed']
    : ['defence.enter', 'defence.minSpeed'];

  const out: Fit[] = [];
  for (const id of ids) {
    const t = TUNABLES.find((x) => x.id === id);
    if (!t) continue;
    const key = GATE_SIGNAL[id];
    const samples = attempts.map((a) => a.peaks[key]).filter((v) => v > 0);
    if (samples.length < MIN_ATTEMPTS) continue;

    const observed = median(samples);
    const raw = observed * (HEADROOM[id] ?? 0.6);
    const def = DEFAULTS[id] ?? t.get();
    const bounded = Math.min(def * 1.15, Math.max(def * 0.4, raw));
    const finalValue = Math.min(t.max, Math.max(t.min, bounded));

    out.push({ tunableId: id, label: t.label, from: t.get(), to: round3(finalValue), observed: round3(observed) });
  }
  return out;
}

export function applyFits(fits: Fit[]): void {
  for (const f of fits) {
    const t = TUNABLES.find((x) => x.id === f.tunableId);
    if (t) t.set(f.to);
  }
}

const round3 = (v: number) => Math.round(v * 1000) / 1000;
